import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/EnhancedSupabaseAuthContext';
import { HeaderLogo } from '../Logo/CommunityLogo';
import { SITE } from '../../config/site';
import { SocialIcons } from '../ui/SocialFollow';
import {
  IconBell, IconBars, IconTimes, IconMapMarkerAlt, IconPhoneAlt, IconSearch, IconUser, IconSignOutAlt,
  IconSignInAlt, IconUserPlus, IconCog, IconChevronRight, IconChevronDown, IconWallet
} from '../ui/icons';
import './HeaderUnified.css';

/**
 * En-tête public du site.
 * - Barre d'informations (localisation, téléphone, langue, compte) : masquée sur téléphone
 * - Barre principale : logo, menu court (le menu « À propos » regroupe les pages institutionnelles),
 *   bouton d'action unique ; menu latéral sous 1280 px
 */

// Le sélecteur de langue reviendra quand le site sera entièrement traduit (src/locales)

// Pages institutionnelles, regroupées sous « À propos »
const ABOUT_NAV = [
  { path: '/about', name: 'Qui sommes-nous', description: 'Notre mission et notre équipe' },
  { path: '/pour-qui', name: 'Pour qui ?', description: 'Investisseurs, diaspora, entrepreneurs, partenaires' },
  { path: '/agenda', name: 'Agenda', description: 'Réunions, ateliers et portes ouvertes' },
  { path: '/blog', name: 'Actualités', description: 'Nouvelles des projets et de la plateforme' },
  { path: '/partnership', name: 'Devenir partenaire', description: 'Institutions, ONG, entreprises' },
  { path: '/recrutement', name: 'Recrutement', description: 'Rejoindre notre équipe' }
];

// Menu principal : les parcours des deux publics (investisseurs et porteurs de projets)
const MAIN_NAV = [
  { path: '/projects', name: 'Projets' },
  { path: '/investissement-revenus', name: 'Investir' },
  { path: '/financement', name: 'Financer mon projet' },
  { path: '/impact-social', name: 'Impact' },
  { name: 'À propos', children: ABOUT_NAV },
  { path: '/contact', name: 'Contact' }
];

// Menu mobile : mêmes liens, groupés par public
const MOBILE_SECTIONS = [
  { title: null, items: [{ path: '/', name: 'Accueil' }] },
  {
    title: 'Investir',
    items: [
      { path: '/projects', name: 'Projets' },
      { path: '/investissement-revenus', name: 'Investir' },
      { path: '/diaspora', name: "Investir depuis l'étranger" },
      { path: '/impact-social', name: 'Impact' }
    ]
  },
  { title: 'Entreprendre', items: [{ path: '/financement', name: 'Financer mon projet' }, { path: '/eligibilite', name: "Test d'éligibilité" }, { path: '/incubateur', name: 'Incubateur du Centre Hub' }] },
  { title: 'À propos', items: [...ABOUT_NAV, { path: '/contact', name: 'Contact' }] }
];

const navLinkClass = (isActive) => `px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
  isActive ? 'bg-white/15 text-white' : 'text-blue-100 hover:text-white hover:bg-white/10'
}`;

/** Menu déroulant accessible : clic ou clavier, fermeture par Échap, clic extérieur ou changement de page */
const DropdownNav = ({ item }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuId = 'menu-a-propos';
  const isActive = item.children.some(child => location.pathname.startsWith(child.path));

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative"
      onBlur={event => { if (!containerRef.current?.contains(event.relatedTarget)) setOpen(false); }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
        aria-controls={menuId}
        className={`${navLinkClass(isActive)} inline-flex items-center gap-1.5`}
      >
        {item.name}
        <IconChevronDown className={`text-xs transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <ul id={menuId} className="absolute left-1/2 -translate-x-1/2 mt-2 w-72 bg-white rounded-xl shadow-2xl ring-1 ring-black/5 p-2 z-50">
          {item.children.map(child => (
            <li key={child.path}>
              <NavLink
                to={child.path}
                className={({ isActive: childActive }) => `block px-4 py-3 rounded-lg ${
                  childActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <span className="block text-sm font-semibold text-gray-900">{child.name}</span>
                <span className="block text-xs text-gray-500">{child.description}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MobileMenu = ({ user, onClose, onLogout }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const handleSearch = (event) => {
    event.preventDefault();
    if (!query.trim()) return;
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 xl:hidden" role="dialog" aria-modal="true" aria-label="Menu de navigation">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-blue-900 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
          <span className="text-lg font-bold text-white">Menu</span>
          <button onClick={onClose} className="text-white p-2 rounded-lg hover:bg-white/10" aria-label="Fermer le menu">
            <IconTimes className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSearch} className="p-4 border-b border-white/10" role="search">
          <div className="relative">
            <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300 text-sm" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Rechercher un projet..."
              aria-label="Rechercher"
              className="w-full bg-white/10 text-white placeholder-blue-200 pl-10 pr-4 py-3 rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
        </form>

        <nav className="flex-1 overflow-y-auto p-3 space-y-3" aria-label="Navigation mobile">
          {MOBILE_SECTIONS.map(section => (
            <div key={section.title || 'accueil'}>
              {section.title && (
                <p className="px-4 pb-1 text-xs font-bold uppercase tracking-wider text-blue-300">{section.title}</p>
              )}
              {section.items.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  onClick={onClose}
                  className={({ isActive }) => `flex items-center justify-between px-4 py-2.5 rounded-lg text-white ${
                    isActive ? 'bg-white/15 font-semibold' : 'hover:bg-white/10'
                  }`}
                >
                  {item.name}
                  <IconChevronRight className="w-3 h-3 text-blue-300" aria-hidden="true" />
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-3">
          {user ? (
            <>
              <p className="text-sm text-blue-100 truncate">{user.email}</p>
              <div className="grid grid-cols-2 gap-2">
                <Link to="/profil" onClick={onClose} className="flex items-center justify-center gap-2 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium">
                  <IconWallet /> Portefeuille
                </Link>
                <Link to="/settings" onClick={onClose} className="flex items-center justify-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium">
                  <IconCog /> Paramètres
                </Link>
              </div>
              <button onClick={() => { onClose(); onLogout(); }} className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-200 hover:bg-red-500/20 rounded-lg text-sm">
                <IconSignOutAlt /> Déconnexion
              </button>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Link to="/login" onClick={onClose} className="flex items-center justify-center gap-2 px-3 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium">
                <IconSignInAlt /> Connexion
              </Link>
              <Link to="/register" onClick={onClose} className="flex items-center justify-center gap-2 px-3 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold">
                <IconUserPlus /> Inscription
              </Link>
            </div>
          )}
          <a href={SITE.phoneHref} className="flex items-center gap-2 text-blue-200 text-sm">
            <IconPhoneAlt aria-hidden="true" /> {SITE.phone} · {SITE.address}
          </a>
        </div>
      </div>
    </div>
  );
};

const HeaderUnified = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Le menu mobile se ferme à chaque changement de page
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const closeMenu = React.useCallback(() => setIsMobileMenuOpen(false), []);

  return (
    <>
      <header className="bg-gradient-to-r from-blue-700 to-blue-900 shadow-lg sticky top-0 z-50">
        {/* Barre d'informations (ordinateur et tablette) */}
        <div className="hidden sm:block border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-between items-center h-9 text-sm text-blue-100">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <IconMapMarkerAlt className="text-blue-300" aria-hidden="true" /> {SITE.address}
              </span>
              <a href={SITE.phoneHref} className="hidden md:flex items-center gap-1.5 hover:text-white">
                <IconPhoneAlt className="text-blue-300 text-xs" aria-hidden="true" /> {SITE.phone}
              </a>
              <SocialIcons className="hidden md:flex" />
            </div>
            <p className="hidden xl:block text-blue-200 font-medium">Votre capital au service de l'impact social</p>
            <div className="flex items-center gap-2">
              {user ? (
                <>
                  <Link to="/notifications" className="p-2 hover:text-white hover:bg-white/10 rounded-lg" aria-label="Notifications" title="Notifications">
                    <IconBell aria-hidden="true" />
                  </Link>
                  <Link to="/dashboard" className="flex items-center gap-1.5 px-2 py-1 hover:text-white hover:bg-white/10 rounded-lg">
                    <IconUser aria-hidden="true" /> Mon espace
                  </Link>
                  <button onClick={signOut} className="flex items-center gap-1.5 px-2 py-1 text-red-200 hover:text-white hover:bg-red-500/20 rounded-lg">
                    <IconSignOutAlt aria-hidden="true" /> Déconnexion
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="flex items-center gap-1.5 px-2 py-1 hover:text-white hover:bg-white/10 rounded-lg">
                    <IconSignInAlt aria-hidden="true" /> Connexion
                  </Link>
                  <Link to="/register" className="flex items-center gap-1.5 px-2 py-1 text-orange-200 hover:text-white hover:bg-white/10 rounded-lg">
                    <IconUserPlus aria-hidden="true" /> Inscription
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Barre principale */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 gap-4">
          <HeaderLogo />

          <nav className="hidden xl:flex items-center gap-1" aria-label="Navigation principale">
            {MAIN_NAV.map(item => (item.children ? (
              <DropdownNav key={item.name} item={item} />
            ) : (
              <NavLink key={item.path} to={item.path} className={({ isActive }) => navLinkClass(isActive)}>
                {item.name}
              </NavLink>
            )))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to={user ? '/profil' : '/projects'}
              className="hidden sm:inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm px-4 py-2 rounded-lg shadow transition-colors whitespace-nowrap"
            >
              {user ? <><IconWallet aria-hidden="true" /> Mon portefeuille</> : 'Investir maintenant'}
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="xl:hidden p-3 text-white rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-orange-400"
              aria-expanded={isMobileMenuOpen}
              aria-label="Ouvrir le menu"
            >
              <IconBars className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && <MobileMenu user={user} onClose={closeMenu} onLogout={signOut} />}
    </>
  );
};

export default HeaderUnified;
